package us.laantech.admin.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DatePicker
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRow
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TimePicker
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.offsetAt
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Modal dialog that lets the user pick a date and time.
 *
 * @param initialEpochMillis Optional initial value (UTC milliseconds since epoch).
 *                           If null, the current selection in the date picker is used
 *                           and the time defaults to 00:00.
 * @param title              Title shown at the top of the dialog.
 * @param is24Hour           Whether the time picker should use 24-hour format.
 * @param onDismiss          Called when the user cancels or dismisses the dialog.
 * @param onConfirm          Called with the chosen UTC epoch milliseconds when confirmed.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DateTimeEntryModal(
    initialEpochMillis: Long? = Clock.System.now().toEpochMilliseconds(),
    title: String = "Select date & time",
    is24Hour: Boolean = false,
    onDismiss: () -> Unit,
    onConfirm: (epochMillis: Long) -> Unit,
) {
    val tz = TimeZone.currentSystemDefault()

    // --- Date state ---
    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = initialEpochMillis,
    )

    // --- Time state ---
    // Derive initial hour/minute from the supplied epoch millis (UTC).
    val initialHour: Int
    val initialMinute: Int
    if (initialEpochMillis != null) {
        val localDateTime = Instant.fromEpochMilliseconds(initialEpochMillis).toLocalDateTime(tz)
        initialHour = localDateTime.hour
        initialMinute = localDateTime.minute
    } else {
        initialHour = 0
        initialMinute = 0
    }
    val timePickerState = rememberTimePickerState(
        initialHour = initialHour,
        initialMinute = initialMinute,
        is24Hour = is24Hour,
    )

    // --- Tabs (Date | Time) ---
    var selectedTab by remember { mutableStateOf(0) }
    val tabs = listOf("Date", "Time")

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(title) },
        text = {
            Column(Modifier.fillMaxWidth()) {
                TabRow(selectedTabIndex = selectedTab) {
                    tabs.forEachIndexed { index, label ->
                        Tab(
                            selected = selectedTab == index,
                            onClick = { selectedTab = index },
                            text = { Text(label) },
                        )
                    }
                }

                Spacer(Modifier.height(8.dp))

                when (selectedTab) {
                    0 -> DatePicker(
                        state = datePickerState,
                        modifier = Modifier.padding(top = 8.dp),
                    )
                    1 -> Row(
                        modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        TimePicker(state = timePickerState)
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    val dateMillis = datePickerState.selectedDateMillis
                    if (dateMillis != null) {
                        val timeMs =
                            timePickerState.hour * 60L * 60L * 1000L +
                                    timePickerState.minute * 60L * 1000L
                        val localMillis = dateMillis + timeMs
                        val offsetSeconds = tz.offsetAt(Instant.fromEpochMilliseconds(localMillis)).totalSeconds
                        onConfirm(localMillis - offsetSeconds * 1000L)
                    }
                },
                enabled = datePickerState.selectedDateMillis != null,
            ) { Text("OK") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel") }
        },
    )
}